import { useEffect, useState } from "react";
import {
  AlertCircle,
  CheckCircle2,
  Globe,
  KeyRound,
  Loader2,
  Plus,
  Terminal,
  Trash2,
  X,
} from "lucide-react";
import { api, type McpCatalogEntry, type McpServerInfo } from "../lib/api.js";

type Tab = "servers" | "find" | "custom";

export function McpManager({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [tab, setTab] = useState<Tab>("servers");
  const [servers, setServers] = useState<McpServerInfo[]>([]);
  const [catalog, setCatalog] = useState<McpCatalogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function refresh() {
    setLoading(true);
    try {
      const d = await api.listMcp();
      setServers(d.servers);
      setCatalog(d.catalog);
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (open) void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!open) return null;

  const configuredIds = new Set(servers.map((s) => s.name));

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal mcp-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-head">
          <span>MCP Servers</span>
          <button className="ghost icon" onClick={onClose} aria-label="Close">
            <X size={16} />
          </button>
        </div>

        <div className="mcp-tabs">
          <button className={tab === "servers" ? "active" : ""} onClick={() => setTab("servers")}>
            Your servers {servers.length > 0 && <span className="pill">{servers.length}</span>}
          </button>
          <button className={tab === "find" ? "active" : ""} onClick={() => setTab("find")}>
            Find
          </button>
          <button className={tab === "custom" ? "active" : ""} onClick={() => setTab("custom")}>
            Add custom
          </button>
        </div>

        <div className="modal-body mcp-body">
          {err && <div className="mcp-error">{err}</div>}
          {loading && (
            <div className="mcp-loading">
              <Loader2 size={16} className="spin" /> Loading…
            </div>
          )}

          {tab === "servers" && !loading && (
            <ServersTab servers={servers} onChanged={refresh} />
          )}
          {tab === "find" && !loading && (
            <FindTab
              catalog={catalog.filter((c) => !configuredIds.has(c.id))}
              onAdded={() => {
                void refresh();
                setTab("servers");
              }}
            />
          )}
          {tab === "custom" && (
            <CustomTab
              onAdded={() => {
                void refresh();
                setTab("servers");
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

function ServersTab({ servers, onChanged }: { servers: McpServerInfo[]; onChanged: () => void }) {
  if (servers.length === 0) {
    return (
      <p className="field-hint">
        No MCP servers yet. Use <strong>Find</strong> to add a popular one, or{" "}
        <strong>Add custom</strong> for your own.
      </p>
    );
  }
  return (
    <div className="mcp-list">
      {servers.map((s) => (
        <ServerRow key={s.name} server={s} onChanged={onChanged} />
      ))}
    </div>
  );
}

function ServerRow({ server, onChanged }: { server: McpServerInfo; onChanged: () => void }) {
  const [busy, setBusy] = useState(false);
  const [authUrl, setAuthUrl] = useState<string | null>(null);
  const [redirect, setRedirect] = useState("");
  const [authMsg, setAuthMsg] = useState<string | null>(null);

  async function remove() {
    if (!confirm(`Remove the "${server.name}" MCP server?`)) return;
    setBusy(true);
    await api.removeMcp(server.name).catch(() => {});
    setBusy(false);
    onChanged();
  }

  async function startAuth() {
    setBusy(true);
    setAuthMsg(null);
    const r = await api.authorizeMcp(server.name).catch((e) => ({ ok: false, summary: String(e) }));
    setBusy(false);
    if (r.ok && /^https?:\/\//.test(r.summary.trim())) {
      setAuthUrl(r.summary.trim());
      window.open(r.summary.trim(), "_blank", "noreferrer");
    } else {
      setAuthMsg(r.summary);
    }
  }

  async function completeAuth() {
    setBusy(true);
    const r = await api.authorizeMcp(server.name, redirect.trim()).catch((e) => ({
      ok: false,
      summary: String(e),
    }));
    setBusy(false);
    setAuthMsg(r.summary);
    if (r.ok) {
      setAuthUrl(null);
      setRedirect("");
      onChanged();
    }
  }

  const StatusIcon = server.ready ? CheckCircle2 : AlertCircle;
  const statusText = server.ready
    ? server.auth === "oauth"
      ? "OAuth"
      : "Ready"
    : `Needs ${server.missingSecrets.join(", ")}`;

  return (
    <div className="mcp-card">
      <div className="mcp-card-main">
        <div className="mcp-card-icon">
          {server.transport === "http" ? <Globe size={16} /> : <Terminal size={16} />}
        </div>
        <div className="mcp-card-text">
          <div className="mcp-card-name">{server.name}</div>
          <div className="mcp-card-sub">
            {server.transport === "http" ? server.url : `${server.command} ${(server.args ?? []).join(" ")}`}
          </div>
        </div>
        <div className={`mcp-status ${server.ready ? "ok" : "warn"}`}>
          <StatusIcon size={13} /> {statusText}
        </div>
      </div>
      <div className="mcp-card-actions">
        {server.auth === "oauth" && (
          <button className="ghost sm" disabled={busy} onClick={startAuth}>
            <KeyRound size={13} /> Authorize
          </button>
        )}
        <button className="ghost sm danger" disabled={busy} onClick={remove}>
          <Trash2 size={13} /> Remove
        </button>
      </div>
      {authUrl && (
        <div className="mcp-auth">
          <p className="field-hint">
            A browser tab opened for approval. After approving, copy the full URL from the address
            bar (it starts with <code>http://localhost</code>) and paste it here:
          </p>
          <div className="mcp-auth-row">
            <input
              value={redirect}
              onChange={(e) => setRedirect(e.target.value)}
              placeholder="http://localhost:…/callback?code=…"
            />
            <button className="primary sm" disabled={busy || !redirect.trim()} onClick={completeAuth}>
              Finish
            </button>
          </div>
        </div>
      )}
      {authMsg && <p className="field-hint">{authMsg}</p>}
    </div>
  );
}

function FindTab({ catalog, onAdded }: { catalog: McpCatalogEntry[]; onAdded: () => void }) {
  if (catalog.length === 0) {
    return <p className="field-hint">All catalog servers are already added.</p>;
  }
  return (
    <div className="mcp-list">
      {catalog.map((c) => (
        <CatalogRow key={c.id} entry={c} onAdded={onAdded} />
      ))}
    </div>
  );
}

function CatalogRow({ entry, onAdded }: { entry: McpCatalogEntry; onAdded: () => void }) {
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const needsToken = !!entry.secret;

  async function add() {
    setBusy(true);
    setErr(null);
    try {
      await api.addMcp({ catalogId: entry.id, token: token.trim() || undefined });
      onAdded();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  return (
    <div className="mcp-card">
      <div className="mcp-card-main">
        <div className="mcp-card-icon">
          {entry.transport === "http" ? <Globe size={16} /> : <Terminal size={16} />}
        </div>
        <div className="mcp-card-text">
          <div className="mcp-card-name">{entry.title}</div>
          <div className="mcp-card-sub">{entry.description}</div>
        </div>
      </div>
      <div className="mcp-card-actions">
        {needsToken && (
          <input
            className="mcp-token"
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder={entry.secret!.label}
          />
        )}
        <button className="primary sm" disabled={busy || (needsToken && !token.trim())} onClick={add}>
          <Plus size={13} /> Add
        </button>
      </div>
      {err && <p className="field-hint mcp-err-text">{err}</p>}
    </div>
  );
}

function CustomTab({ onAdded }: { onAdded: () => void }) {
  const [name, setName] = useState("");
  const [type, setType] = useState<"stdio" | "http">("stdio");
  const [command, setCommand] = useState("npx");
  const [args, setArgs] = useState("");
  const [url, setUrl] = useState("");
  const [auth, setAuth] = useState<"none" | "bearer" | "oauth">("none");
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function add() {
    setBusy(true);
    setErr(null);
    try {
      const def: Record<string, unknown> = { lifecycle: "lazy" };
      const secrets: Record<string, string> = {};
      const envName = `${name.trim().toUpperCase().replace(/[^A-Z0-9]+/g, "_")}_TOKEN`;
      if (type === "stdio") {
        def.command = command.trim();
        def.args = args.trim() ? args.trim().split(/\s+/) : [];
      } else {
        def.url = url.trim();
      }
      if (auth === "bearer" && token.trim()) {
        def.auth = "bearer";
        def.bearerTokenEnv = envName;
        secrets[envName] = token.trim();
      } else if (auth === "oauth") {
        def.auth = "oauth";
      }
      await api.addMcp({ name: name.trim(), def, secrets });
      onAdded();
    } catch (e) {
      setErr((e as Error).message);
      setBusy(false);
    }
  }

  const valid = name.trim() && (type === "stdio" ? command.trim() : url.trim());

  return (
    <div className="mcp-form">
      <label className="field">
        <span className="field-label">Name</span>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="my-server" />
      </label>
      <label className="field">
        <span className="field-label">Transport</span>
        <select value={type} onChange={(e) => setType(e.target.value as "stdio" | "http")}>
          <option value="stdio">stdio (local command)</option>
          <option value="http">http (remote url)</option>
        </select>
      </label>
      {type === "stdio" ? (
        <>
          <label className="field">
            <span className="field-label">Command</span>
            <input value={command} onChange={(e) => setCommand(e.target.value)} placeholder="npx" />
          </label>
          <label className="field">
            <span className="field-label">Arguments</span>
            <input
              value={args}
              onChange={(e) => setArgs(e.target.value)}
              placeholder="-y some-mcp-server@latest"
            />
          </label>
        </>
      ) : (
        <label className="field">
          <span className="field-label">URL</span>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://example.com/mcp"
          />
        </label>
      )}
      <label className="field">
        <span className="field-label">Authentication</span>
        <select value={auth} onChange={(e) => setAuth(e.target.value as "none" | "bearer" | "oauth")}>
          <option value="none">None</option>
          <option value="bearer">Bearer token / API key</option>
          <option value="oauth">OAuth (authorize after adding)</option>
        </select>
      </label>
      {auth === "bearer" && (
        <label className="field">
          <span className="field-label">Token</span>
          <input
            type="password"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Stored securely, not committed to git"
          />
        </label>
      )}
      {err && <p className="field-hint mcp-err-text">{err}</p>}
      <button className="primary" disabled={busy || !valid} onClick={add}>
        {busy ? "Adding…" : "Add server"}
      </button>
      <p className="field-hint">
        Secrets are stored on the server in a gitignored file and injected into the worker — never
        committed. Adding a server restarts the worker so it picks up the new tools.
      </p>
    </div>
  );
}
